import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";

import Loader from "@/components/loader";
import api from "@/services/api";
import { ViewTabs, ColorLegend, getStatusColor, fmt, invoiceNumber, TIRANA_CUTOFF, isTirana } from "./shared";

// Money still owed on an invoice: totalRemaining when set (partial payments), otherwise the full total (received is "no" anyway).
const remainingOf = (inv) => inv.totalRemaining || inv.total || 0;
// Positive = days overdue, negative/zero = not due yet.
const daysLate = (inv) => (inv.dueAt ? Math.floor((Date.now() - new Date(inv.dueAt)) / (1000 * 60 * 60 * 24)) : 0);

// Same lateness scale everywhere: gray = not due, yellow = 1-30, orange = 31-60, red = 60+.
const BUCKETS = [
  { key: "notdue", label: "Not due yet", tone: "bg-gray-100 text-gray-700", test: (d) => d <= 0 },
  { key: "b30", label: "1-30 days late", tone: "bg-yellow-100 text-yellow-800", test: (d) => d > 0 && d <= 30 },
  { key: "b60", label: "31-60 days late", tone: "bg-orange-100 text-orange-800", test: (d) => d > 30 && d <= 60 },
  { key: "b60p", label: "60+ days late", tone: "bg-red-100 text-red-800", test: (d) => d > 60 },
];

const lateBadge = (d) => {
  if (d <= 0) return <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">due in {-d}d</span>;
  const tone = d <= 30 ? "bg-yellow-100 text-yellow-800" : d <= 60 ? "bg-orange-100 text-orange-800" : "bg-red-100 text-red-800";
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${tone}`}>{d}d late</span>;
};

// `embedded` : la même page servie comme onglet de la section Admin (à côté du PNL et de la
// Tréso). Elle y perd son sélecteur de vues cashout — deux barres d'onglets l'une sous l'autre,
// dont une qui fait sortir de la section, ne se lisent pas.
export default function Recouvrement({ embedded = false }) {
  const [invoices, setInvoices] = useState(null);
  const navigate = useNavigate();
  const { businessUnit } = useSelector((state) => state.Auth);
  // Post-cutoff defaults to on for Tirana (its books start Jan 2026) but is available everywhere.
  const [postCutoff, setPostCutoff] = useState(isTirana(businessUnit));
  const [overdueOnly, setOverdueOnly] = useState(false);

  useEffect(() => {
    fetchInvoices();
  }, [businessUnit._id, postCutoff, overdueOnly]);

  async function fetchInvoices() {
    try {
      const buFilter = businessUnit._id !== "ALL" ? { business_unit_id: businessUnit._id } : {};
      const cutoffFilter = postCutoff ? { dateFrom: TIRANA_CUTOFF } : {};
      const overdueFilter = overdueOnly ? { late_invoice: true } : {};
      const { ok, data } = await api.post("/invoice/search", { sent: "yes", received: "no", limit: 10000, ...buFilter, ...cutoffFilter, ...overdueFilter });
      if (!ok) return toast.error("Something went wrong!");
      setInvoices(data);
    } catch (e) {
      console.log(e);
      toast.error("Something went wrong!");
    }
  }

  if (!invoices) return <Loader />;

  const totalOutstanding = invoices.reduce((acc, inv) => acc + remainingOf(inv), 0);

  // Aging buckets
  const bucketTotals = BUCKETS.map((b) => ({
    ...b,
    total: invoices.filter((inv) => b.test(daysLate(inv))).reduce((acc, inv) => acc + remainingOf(inv), 0),
    count: invoices.filter((inv) => b.test(daysLate(inv))).length,
  }));

  // Group by client (projectName is the client in this table, like the cashout view)
  const byClient = {};
  for (const inv of invoices) {
    const key = inv.projectName || inv.customer_name || "Unknown";
    if (!byClient[key]) byClient[key] = { client: key, invoices: [], total: 0, overdue: 0, oldestLate: 0 };
    byClient[key].invoices.push(inv);
    byClient[key].total += remainingOf(inv);
    const late = daysLate(inv);
    if (late > 0) {
      byClient[key].overdue += remainingOf(inv);
      byClient[key].oldestLate = Math.max(byClient[key].oldestLate, late);
    }
  }
  const clients = Object.values(byClient).sort((a, b) => b.total - a.total);
  for (const c of clients) c.invoices.sort((a, b) => new Date(a.dueAt || 0) - new Date(b.dueAt || 0));

  return (
    <div className={`space-y-4 ${embedded ? "py-4" : "p-4"}`}>
      {/* Header: what & how much on the left, navigation on the right */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">
          Recouvrement{" "}
          <span className="text-lg font-semibold text-gray-500">
            · {fmt(totalOutstanding)} to collect{postCutoff ? " since Jan 2026" : ""}
          </span>
        </h1>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer" title="Only invoices dated Jan 1, 2026 or later — money Tirana is responsible for">
            <input type="checkbox" checked={postCutoff} onChange={(e) => setPostCutoff(e.target.checked)} className="rounded border-gray-300" />
            Post-cutoff only (≥ Jan 2026)
          </label>
          <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer" title="Only invoices whose due date has passed">
            <input type="checkbox" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} className="rounded border-gray-300" />
            Overdue only
          </label>
          {embedded ? null : <ViewTabs />}
        </div>
      </div>

      {/* Aging summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <div className="text-xs text-gray-500">To be received</div>
          <div className="text-lg font-bold">{fmt(totalOutstanding)}</div>
          <div className="text-xs text-gray-400">
            {invoices.length} invoices · {clients.length} clients
          </div>
        </div>
        {bucketTotals.map((b) => (
          <div key={b.key} className={`rounded-lg border border-gray-200 p-3 ${b.tone}`}>
            <div className="text-xs opacity-80">{b.label}</div>
            <div className="text-lg font-bold">{fmt(b.total)}</div>
            <div className="text-xs opacity-70">{b.count} invoices</div>
          </div>
        ))}
      </div>

      <ColorLegend />

      {/* Table grouped by client, biggest debt first */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-3 py-2 text-left text-gray-600 font-medium">Client / Invoice</th>
              <th className="px-3 py-2 text-left text-gray-600 font-medium">Company</th>
              <th className="px-3 py-2 text-left text-gray-600 font-medium">Sent</th>
              <th className="px-3 py-2 text-left text-gray-600 font-medium">Due</th>
              <th className="px-3 py-2 text-left text-gray-600 font-medium">Late</th>
              <th className="px-3 py-2 text-left text-gray-600 font-medium">Collection status</th>
              <th className="px-3 py-2 text-right text-gray-600 font-medium">To receive</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <React.Fragment key={c.client}>
                <tr className="bg-gray-100 border-t-2 border-gray-200">
                  <td className="px-3 py-2 font-bold text-sm text-gray-900" colSpan={4}>
                    {c.client} <span className="font-normal text-gray-500">({c.invoices.length} invoices)</span>
                  </td>
                  <td className="px-3 py-2">{c.oldestLate > 0 ? lateBadge(c.oldestLate) : null}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{c.overdue > 0 ? `${fmt(c.overdue)} overdue` : ""}</td>
                  <td className="px-3 py-2 text-right font-bold text-sm tabular-nums">{fmt(c.total)}</td>
                </tr>
                {c.invoices.map((inv) => (
                  <tr
                    key={inv._id}
                    onClick={(ev) => (ev.metaKey || ev.ctrlKey ? window.open(`/invoice/${inv._id}/collection`, "_blank") : navigate(`/invoice/${inv._id}/collection`))}
                    title="Open invoice collection"
                    className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-3 py-1.5 pl-6 text-gray-700">
                      <Link to={`/invoice/${inv._id}/collection`} onClick={(ev) => ev.stopPropagation()} className="font-semibold text-blue-600 hover:underline">
                        {invoiceNumber(inv) || "N/A"}
                      </Link>{" "}
                      · {inv.name}
                    </td>
                    <td className="px-3 py-1.5 text-gray-500 whitespace-nowrap">{inv.organisation_name}</td>
                    <td className="px-3 py-1.5 text-gray-500 whitespace-nowrap">{inv.sentAt ? new Date(inv.sentAt).toLocaleDateString("fr-FR") : "—"}</td>
                    <td className="px-3 py-1.5 text-gray-500 whitespace-nowrap">{inv.dueAt ? new Date(inv.dueAt).toLocaleDateString("fr-FR") : "—"}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{lateBadge(daysLate(inv))}</td>
                    <td className="px-3 py-1.5">
                      {inv.collection_status ? (
                        <span className={`px-2 py-0.5 rounded-full text-xs whitespace-nowrap ${getStatusColor(inv.collection_status)}`}>{inv.collection_status}</span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                      {/* Abandonnée : toujours à recouvrer ici, mais sortie du prévisionnel de trésorerie. */}
                      {inv.written_off ? (
                        <span
                          title="Sortie du prévisionnel de trésorerie du pilotage — toujours due"
                          className="ml-1 px-2 py-0.5 rounded-full text-xs whitespace-nowrap bg-gray-200 text-gray-600"
                        >
                          hors tréso
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{fmt(remainingOf(inv))}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-300 font-bold text-sm">
              <td className="px-3 py-2" colSpan={6}>
                Total ({invoices.length} invoices)
              </td>
              <td className="px-3 py-2 text-right tabular-nums">{fmt(totalOutstanding)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
